import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { App } from '@/App';
import { leerConfiguracion } from '@/configuracion/entorno';
import { puertoFirebase } from '@/infra/callables';
import type { PuertoReservas } from '@/infra/contratos';
import { crearServicios, type ServiciosFirebase } from '@/infra/firebase';
import { ContextoPuerto } from '@/publico/estado/puerto';
import '@/estilos/base.css';
import '@/estilos/publico.css';

const configuracion = leerConfiguracion(import.meta.env, {
  esDesarrollo: import.meta.env.DEV,
});

if (!configuracion.ok) {
  // Ademas de la pantalla, queda en la consola para quien este mirando las
  // herramientas de desarrollo. Ver docs/ESTADO.md y apps/web/.env.example.
  console.error(configuracion.error.mensaje);
}

/**
 * Puerto real hacia las Cloud Functions.
 *
 * Sin configuracion valida no se construye: la aplicacion muestra la pantalla de error y
 * no hay nada que llamar. Es tambien lo que hace que un `.env` a medias falle de forma
 * ruidosa en vez de intentar hablar con un proyecto inexistente (CLAUDE.md §5).
 */
const servicios: ServiciosFirebase | null = configuracion.ok
  ? crearServicios(configuracion.valor)
  : null;
const puerto: PuertoReservas | null = servicios === null ? null : puertoFirebase(servicios);

const clienteConsultas = new QueryClient({
  defaultOptions: {
    queries: {
      // La disponibilidad cambia sola: alguien puede tomar el turno mientras la pantalla
      // esta abierta. Se revalida al volver a la pestana y se considera fresca 30 s.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const contenedor = document.getElementById('raiz');

if (!contenedor) {
  throw new Error('No se encontro el elemento #raiz en index.html.');
}

createRoot(contenedor).render(
  <StrictMode>
    <QueryClientProvider client={clienteConsultas}>
      <ContextoPuerto.Provider value={puerto}>
        <BrowserRouter>
          <App configuracion={configuracion} />
        </BrowserRouter>
      </ContextoPuerto.Provider>
    </QueryClientProvider>
  </StrictMode>,
);
